import { useCallback, useEffect } from 'react'

import { useSyncExternalStore } from 'use-sync-external-store/shim'

import { getLocalStorageItem } from './get-local-storage-item'
import { removeLocalStorageItem } from './remove-local-storage-item'
import { setLocalStorageItem } from './set-local-storage-item'

export function useLocalStorage(key: string, initialValue?: string) {
  const getSnapshot = () => getLocalStorageItem(key)

  const store = useSyncExternalStore(callback => {
    window.addEventListener('storage', callback)
    return () => window.removeEventListener('storage', callback)
  }, getSnapshot)

  const setState = useCallback(
    (v: string | ((val: string | null) => string)) => {
      try {
        const nextState = typeof v === 'function' ? v(store) : v

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key)
        } else {
          setLocalStorageItem(key, nextState)
        }
      } catch (e) {
        console.warn(e)
      }
    },
    [key, store],
  )

  useEffect(() => {
    if (
      getLocalStorageItem(key) === null &&
      typeof initialValue !== 'undefined'
    ) {
      setLocalStorageItem(key, initialValue)
    }
  }, [key, initialValue])

  return [store || initialValue, setState] as [string, typeof setState]
}
